import React, { useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { Box } from "@mui/material";
import { Plus, Edit, Trash2, Sailboat, Ship } from "lucide-react";
import DeleteConfirmationPopup from "./DeleteConfirmation";
import AddEditFleetModal from "./AddEditFleetModal";
import VesselListDialog from "./VesselListDialog";
import { AllCommunityModule, ModuleRegistry, themeQuartz, colorSchemeLight } from "ag-grid-community";

const fleetGridTheme = themeQuartz.withPart(colorSchemeLight).withParams({
  backgroundColor: "#ffffff",
  foregroundColor: "#1f2937",
  accentColor: "#3b82f6",
  headerTextColor: "#ffffff",
  headerBackgroundColor: "#1a325d",
  oddRowBackgroundColor: "#f9fafb",
  chromeBackgroundColor: "#f9fafb",
});

ModuleRegistry.registerModules([AllCommunityModule]);

const FleetTable = ({ fleets, onDelete, onFleetAdded }) => {
  const [search, setSearch] = useState("");
  const [deletePopup, setDeletePopup] = useState({
    isOpen: false,
    fleet: null,
  });
  const [addFleetModalOpen, setAddFleetModalOpen] = useState(false);
  const [selectedFleet, setSelectedFleet] = useState(null);
  const [isVesselsModalOpen, setIsVesselsModalOpen] = useState(false);
  const [selectedVessels, setSelectedVessels] = useState([]);
  const [modalMode, setModalMode] = useState("add");
  const gridRef = useRef();

  const handleEdit = (fleet) => {
    setSelectedFleet(fleet);
    setModalMode("edit");
    setAddFleetModalOpen(true);
  };

  const handleDeleteClick = (fleet) => {
    setDeletePopup({ isOpen: true, fleet });
  };

  const handleDeleteConfirm = () => {
    if (deletePopup.fleet) {
      onDelete?.(deletePopup.fleet.fleetId);
      setDeletePopup({ isOpen: false, fleet: null });
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Fleet Name",
        field: "fleetName",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          fontWeight: "bold",
        },
        cellRenderer: (params) => (
          <div className="flex items-center h-full">
            <span className="text-gray-800 font-medium">{params.value}</span>
          </div>
        ),
      },
      {
        headerName: "Fleet Code",
        field: "fleetCode",
        cellRenderer: (params) => (
          <div className="flex items-center h-full">
            <span className="text-sm font-medium">{params.value || "N/A"}</span>
          </div>
        ),
      },
      {
        headerName: "Company",
        field: "company",
        cellRenderer: (params) => (
          <div className="flex items-center h-full">
            <span className="text-sm font-medium">
              {params.value.name || "N/A"}
            </span>
          </div>
        ),
      },
      {
        headerName: "Region",
        field: "fleetRegion",
        cellRenderer: (params) => (
          <div className="flex items-center h-full">
            <span className="text-sm font-medium">{params.value || "N/A"}</span>
          </div>
        ),
      },
      // {
      //   headerName: "Owner",
      //   field: "owner",
      //   cellRenderer: (params) => (
      //     <div className="flex items-center h-full">
      //       <span className="text-sm font-medium">{params.value || "N/A"}</span>
      //     </div>
      //   ),
      // },
      // {
      //   headerName: "Manager",
      //   field: "manager",
      //   cellRenderer: (params) => (
      //     <div className="flex items-center h-full">
      //       <span className="text-sm font-medium">{params.value || "N/A"}</span>
      //     </div>
      //   ),
      // },
      {
        headerName: "Number of Vessels",
        field: "vessels",
        width: 120,
        cellRenderer: (params) => {
          const vesselCount = params.value?.length || 0;
          return (
            <div
              className={`flex items-center justify-center h-full group`}
              onClick={() => {
                setSelectedVessels(params.value || []);
                setIsVesselsModalOpen(true);
              }}
            >
              <div
                className={`px-4 py-1 rounded-full bg-blue-100 text-blue-700 transition-all cursor-pointer flex items-center gap-2 group-hover:shadow-xs group-hover:ring-1 group-hover:ring-blue-200`}
              >
                <span className="text-sm font-medium">{vesselCount}</span>
              </div>
            </div>
          );
        },
        headerClass: "ag-center-header",
      },
      {
        headerName: "Actions",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        cellRenderer: (params) => (
          <div className="flex items-center gap-2 h-full">
            <button
              onClick={() => handleEdit(params.data)}
              className="group p-2 rounded-full bg-white shadow-md hover:bg-blue-100 transition-all duration-200 border border-blue-200 hover:shadow-blue-300"
            >
              <Edit className="w-4 h-4 text-blue-600 group-hover:scale-110 group-hover:text-blue-700 transition-all" />
            </button>
            <button
              onClick={() => handleDeleteClick(params.data)}
              className="group p-2 rounded-full bg-white shadow-md hover:bg-rose-100 transition-all duration-200 border border-rose-200 hover:shadow-rose-300"
            >
              <Trash2 className="w-4 h-4 text-rose-500 group-hover:scale-110 group-hover:text-rose-700 transition-all" />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="flex items-center gap-2 text-[24px] font-semibold text-slate-800 tracking-tight">
          <Sailboat className="w-6 h-6 text-[#27ade3]" strokeWidth={2} />
          Fleet Management
        </h2>

        <button
          onClick={() => setAddFleetModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-tr from-[#1a325d] to-[#27ade3] shadow-md hover:scale-105 transition-transform rounded-lg"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          Add Fleet
        </button>
      </div>

      {/* Table Container */}
      <Box
        className="ag-theme-quartz"
        sx={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          "& .ag-pinned-left-cols-container": {
            boxShadow: "4px 0 6px -4px rgba(0,0,0,0.1)",
          },
          "& .ag-pinned-right-cols-container": {
            boxShadow: "-4px 0 6px -4px rgba(0,0,0,0.1)",
          },
        }}
      >
        <AgGridReact
          ref={gridRef}
          theme={fleetGridTheme}
          rowData={fleets}
          columnDefs={columnDefs}
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 20, 50]}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: true,
            cellStyle: {
              display: "flex",
              alignItems: "center",
            },
          }}
          suppressHorizontalScroll={false}
          domLayout="autoHeight"
          rowHeight={60}
          headerHeight={50}
          onGridReady={(params) => {
            params.api.sizeColumnsToFit();
            setTimeout(() => {
              const header = document.querySelector(".ag-header");
              if (header) {
                header.style.background =
                  "linear-gradient(to top right, #1a325d, #27ade3)";
                header.style.color = "#ffffff";
              }
            }, 0);
          }}
          suppressDragLeaveHidesColumns={true}
        />
      </Box>

      {/* Modals */}
      <DeleteConfirmationPopup
        isOpen={deletePopup.isOpen}
        onClose={() => setDeletePopup({ isOpen: false, fleet: null })}
        onConfirm={handleDeleteConfirm}
        fleetName={deletePopup.fleet ? `${deletePopup.fleet.fleetName}` : ""}
      />

      {addFleetModalOpen && (
        <AddEditFleetModal
          open={addFleetModalOpen}
          onClose={() => {
            setAddFleetModalOpen(false);
            setSelectedFleet(null);
            setModalMode("add");
          }}
          onSubmit={() => {
            setAddFleetModalOpen(false);
            setSelectedFleet(null);
            setModalMode("add");
            onFleetAdded?.();
          }}
          mode={modalMode}
          fleet={selectedFleet}
        />
      )}

      <VesselListDialog
        open={isVesselsModalOpen}
        onClose={() => setIsVesselsModalOpen(false)}
        vessels={selectedVessels}
      />
    </Box>
  );
};

export default FleetTable;
